import { Fragment, useEffect, useRef, useState } from "react";

import { addProps, classNames } from "../../utils";
import Button from "./Button";
import ButtonLink from "./ButtonLink";
import DropdownButton from "./DropdownButton";

function Table({
  children,
  freezeColumns,
  narrow,
}: {
  children: React.ReactNode;
  freezeColumns?: boolean;
  narrow?: boolean;
}) {
  const tableRef = useRef<HTMLTableElement>(null);
  const [colWidths, setColWidths] = useState<null | number[]>(null);
  const prevColCountRef = useRef<number>(0);

  // Measure column widths once there is at least one body row
  useEffect(() => {
    if (!freezeColumns || colWidths) return;

    const table = tableRef.current;
    if (!table) return;

    const tbody = table.querySelector("tbody");
    if (!tbody || tbody.querySelectorAll("tr").length === 0) return;

    const ths = table.querySelectorAll("thead th");
    if (ths.length === 0) return;

    const widths = Array.from(ths).map((th) => Math.round(th.getBoundingClientRect().width));
    setColWidths(widths);
  }, [freezeColumns, colWidths, children]);

  // Reset widths on window resize
  useEffect(() => {
    if (!freezeColumns) return;
    const handler = () => {
      setColWidths(null);
    };
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("resize", handler);
    };
  }, [freezeColumns]);

  // Reset when # of columns changes
  useEffect(() => {
    if (!freezeColumns) return;
    const table = tableRef.current;
    if (!table) return;
    const currentCount = table.querySelectorAll("thead th").length;
    if (prevColCountRef.current > 0 && currentCount !== prevColCountRef.current) setColWidths(null);

    prevColCountRef.current = currentCount;
  }, [children, freezeColumns]);

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 dark:divide-black/20" ref={tableRef}>
        {colWidths && (
          <colgroup>
            {colWidths.map((w, i) => (
              <col key={i} style={{ width: `${w}px` }} />
            ))}
          </colgroup>
        )}
        {addProps(children, [Table.Header, Table.Body, Table.Footer], { narrow })}
      </table>
    </div>
  );
}

export default Table;

Table.Header = ({ children, narrow }: { children: React.ReactNode; narrow?: boolean }) => (
  <thead className="bg-slate-100/80 dark:bg-slate-900/10">
    {addProps(children, Table.Row, { header: true, narrow })}
  </thead>
);

Table.Footer = ({ children, narrow }: { children: React.ReactNode; narrow?: boolean }) => (
  <tfoot className="bg-slate-100/80 dark:bg-gray-700/20">{addProps(children, Table.Row, { narrow })}</tfoot>
);

Table.Body = ({ children, narrow }: { children: React.ReactNode; narrow?: boolean }) => (
  <tbody className="divide-y divide-slate-200 bg-white dark:divide-black/20 dark:bg-gray-800">
    {addProps(children, Table.Row, { narrow })}
  </tbody>
);

Table.Row = ({
  active,
  children,
  header,
  narrow,
  onClick,
  opaque,
  verticalAlign,
}: {
  active?: boolean;
  children: React.ReactNode;
  header?: boolean;
  narrow?: boolean;
  onClick?: () => void;
  opaque?: boolean;
  verticalAlign?: "bottom" | "middle" | "top";
}) => (
  <tr className="group/table-row">
    {addProps(children, Table.Cell, { active, header, narrow, onClick, opaque, verticalAlign })}
  </tr>
);

Table.Cell = ({
  active,
  align,
  children,
  className,
  header,
  invisible,
  narrow,
  onClick,
  opaque,
  shrink,
  subCell,
  verticalAlign,
  ...props
}: {
  active?: boolean;
  align?: "center" | "left" | "right";
  children?: React.ReactNode;
  className?: string;
  colSpan?: number;
  header?: boolean;
  invisible?: boolean;
  narrow?: boolean;
  onClick?: () => void;
  opaque?: boolean;
  shrink?: boolean;
  subCell?: boolean;
  verticalAlign?: "bottom" | "middle" | "top";
}) => {
  header ??= false;
  invisible ??= false;
  header ??= false;
  align ??= "left";
  verticalAlign ??= "top";
  shrink ??= false;
  opaque ??= false;
  subCell ??= false;

  const classes: Array<false | string | undefined> = [
    align === "left" && "text-left",
    align === "right" && "text-right",
    align === "center" && "text-center",
    verticalAlign === "top" && "align-top",
    verticalAlign === "middle" && "align-middle",
    verticalAlign === "bottom" && "align-bottom",
    shrink && "w-1 whitespace-nowrap",
    active && "bg-gray-100 dark:bg-gray-700",
    narrow ? "px-2 py-2.5" : "px-3 py-3.5",
    onClick && "cursor-pointer group-hover/table-row:bg-gray-100 dark:group-hover/table-row:bg-gray-700",
    opaque && "opacity-50",
    subCell && "shadow-[inset_3px_0_0_0_#8D5AF5]",
    className,
  ];

  children = addProps(children, [Button, ButtonLink, DropdownButton], { size: narrow ? "xs" : "sm" });

  if (invisible) children = <span className="sr-only">{children}</span>;

  if (header) {
    return (
      <th
        className={classNames("text-sm font-semibold text-gray-900 dark:text-white", ...classes)}
        onClick={onClick}
        scope="col"
        {...props}
      >
        {children}{" "}
      </th>
    );
  }

  return (
    <td className={classNames("text-gray-500 dark:text-gray-100", ...classes)} onClick={onClick} {...props}>
      {children}
    </td>
  );
};

Table.StrongText = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <span className={classNames("mt-1 text-sm font-semibold leading-5 text-gray-900 dark:text-white", className)}>
    {children}
  </span>
);

Table.SubText = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <span className={classNames("block text-xs leading-5 text-gray-500 dark:text-gray-400", className)}>{children}</span>
);

Table.SubCellTitle = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <span
    className={classNames("block mt-4 mb-3 text-sm font-semibold leading-5 text-gray-900 dark:text-white", className)}
  >
    {children}
  </span>
);

Table.SubCellWell = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <div className={classNames("p-2 bg-slate-100 dark:bg-black/10 rounded", className)}>{children}</div>
);

Table.Empty = () => <span className="inline-block h-0 w-8 border-b align-middle dark:border-gray-700" />;

Table.MultilineContent = ({ children }: { children: Array<React.ReactNode | undefined> | undefined }) => {
  if (!children || children.filter(Boolean).length === 0) return <Table.Empty />;

  return children.filter(Boolean).map((child, index) => {
    if (index === 0) return <Fragment key={index}>{child}</Fragment>;

    return <Table.SubText key={index}>{child}</Table.SubText>;
  });
};

Table.Definition = ({ children, title }: { children: React.ReactNode; title: string }) => {
  return (
    <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4" data-role="definition">
      <dt className="text-sm font-semibold text-gray-500 dark:text-gray-400">{title}</dt>
      <dd className="underlined-links text-sm text-gray-700 sm:col-span-2 sm:mt-0 dark:text-gray-300">{children}</dd>
    </div>
  );
};
